import { useEffect, useState } from "react";
import { Alert, Button, CircularProgress, Container, FormControl, IconButton, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { Buffer } from "buffer";
import dayjs from "dayjs";

import ShipmentPacketTable from "../../tables/shipmentPacketTable";

import { IGeneratedDocument } from "../../../types/document";
import { ISelectOption } from "../../../types/list";
import { IAddedShipmentDocument, IShipmentDocument, IShipmentPacketSearchRequest, IShipmentPacketSearchValueSets } from "../../../types/shipmentPacket";

import { AppConfig } from "../../../config/appConfig";
import { useListContext } from "../../../contexts/listContext";
import { ErrorProvider } from "../../../classes/errorProvider";

import { DocumentService } from "../../../services/documentService";
import { ShipmentService } from "../../../services/shipmentService";
import { ShipmentPacketService } from "../../../services/shipmentPacketService";
import { UtilityService } from "../../../services/utilityService";

import Navigation from "../../header";
import GeneratedDocumentViewer from "../../generatedDocumentViewer";
import PermissionGate from "../../permissionGate";
import WorkingModal, { IWorkingModalProps } from "../../workingModal";

import "../../../styles/entity/shipmentPacketList.scss";

const ShipmentPacketList = () => {
	const listContext = useListContext();
	const shipmentEntityId = listContext.entityTypes.find((et) => et.name == "Shipment")?.value || undefined;
	const documentTypeFilterOptions = shipmentEntityId ? listContext.documentTypes.filter((dt) => dt.type == shipmentEntityId) : []
	const [gridView, setGridView] = useState<IShipmentDocument[] | undefined>(undefined);
	const [startDate, setStartDate] = useState<dayjs.Dayjs>(dayjs().subtract(1, 'month'));
	const [endDate, setEndDate] = useState<dayjs.Dayjs>(dayjs());
	const [documentTypeFilter, setDocumentTypeFilter] = useState("All");
	const [bolFilter, setBolFilter] = useState("");
	const [customerFilter, setCustomerFilter] = useState("All");
	const [customerFilterOptions, setCustomerFilterOptions] = useState<ISelectOption[]>([]);
	const [vendorFilter, setVendorFilter] = useState("All");
	const [vendorFilterOptions, setVendorFilterOptions] = useState<ISelectOption[]>([]);
	const [includedDocumentTypeIds, setIncludedDocumentTypeIds] = useState<string[]>([]);
	const [includedVendorIds, setIncludedVendorIds] = useState<string[]>([]);
	const [includedCustomerIds, setIncludedCustomerIds] = useState<string[]>([]);

	const [isSearchingShipmentDocuments, setIsSearchingShipmentDocuments] = useState(false);
	const [isLoadingValueSets, setIsLoadingValueSets] = useState(false);
	const [isProcessingOutput, setIsProcessingOutput] = useState(false);
	const [workingModalParams, setWorkingModalParams] = useState<IWorkingModalProps | undefined>(undefined);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	const [combinedDocumentContents, setCombinedDocumentContents] = useState<IAddedShipmentDocument[]>([]);
	const [generatedDocument, setGeneratedDocument] = useState<IGeneratedDocument | undefined>(undefined);

	useEffect(() => {
		loadValueSets();
	}, []);

	// Don't let start date come after end date
	useEffect(() => {
		if (startDate && endDate && startDate.isAfter(endDate)) {
			const temp = startDate;
			setStartDate(endDate);
			setEndDate(temp);
		}
	}, [startDate, endDate]);

	useEffect(() => {
		setGeneratedDocument(undefined);
	}, [combinedDocumentContents])

	useEffect(() => {
		if (!gridView) return;
		const filteredGridView = gridView.filter((item) => {
			if (documentTypeFilter && documentTypeFilter != "All" && item.documentTypeId.toString() != documentTypeFilter) {
				return false;
			}
			if (vendorFilter && vendorFilter != "All" && item.vendorId.toString() != vendorFilter) {
				return false;
			}
			if (customerFilter && customerFilter != "All" && item.customerId.toString() != customerFilter) {
				return false;
			}
			return true;
		});
		let includeddocumentTypeIds: string[] = [];
		let vendorIds: string[] = [];
		let customerIds: string[] = [];
		for (let packet of filteredGridView) {
			if (packet.documentTypeId && !includeddocumentTypeIds.includes(packet.documentTypeId.toString())) {
				includeddocumentTypeIds.push(packet.documentTypeId.toString());
			}
			if (packet.vendorId && !vendorIds.includes(packet.vendorId.toString())) {
				vendorIds.push(packet.vendorId.toString());
			}
			if (packet.customerId && !customerIds.includes(packet.customerId.toString())) {
				customerIds.push(packet.customerId.toString());
			}
		}
		setIncludedDocumentTypeIds(includeddocumentTypeIds);
		setIncludedVendorIds(vendorIds);
		setIncludedCustomerIds(customerIds);
	}, [gridView, documentTypeFilter, customerFilter, vendorFilter]);

	const loadValueSets = async () => {
		setIsLoadingValueSets(true);
		ShipmentService.getSearchValueSets()
			.then((response) => {
				console.log('ShipmentService.getSearchValueSets: SUCCESS', response);
				var valueSets = response.data as IShipmentPacketSearchValueSets;

				setVendorFilterOptions(valueSets.vendors);
				setCustomerFilterOptions(valueSets.customers);
			})
			.catch((error) => {
				console.log('ShipmentService.getSearchValueSets: ERROR', error);
				if (error?.code == "ERR_NETWORK") {
					setErrorMessage(AppConfig.apiConnectionErrorMessage)
				}
				else {
					setErrorMessage(ErrorProvider.getError(error))
				}
			})
			.finally(() => {
				setIsLoadingValueSets(false);
			});
	};

	const searchShipmentDocuments = async () => {
		setIsSearchingShipmentDocuments(true);
		setErrorMessage(undefined);

		const request: IShipmentPacketSearchRequest = {
			documentTypeId: documentTypeFilter && documentTypeFilter != "All" ? documentTypeFilter : undefined,
			bol: bolFilter ? bolFilter : undefined,
			customerId: customerFilter && customerFilter != "All" ? parseInt(customerFilter) : undefined,
			vendorId: vendorFilter && vendorFilter != "All" ? parseInt(vendorFilter) : undefined,
			startDate: startDate ? startDate.toDate() : undefined,
			endDate: endDate ? endDate.toDate() : undefined,
		};

		ShipmentPacketService.searchShipmentDocuments(request)
			.then((response) => {
				console.log('ShipmentPacketService.searchShipmentDocuments: SUCCESS', response);
				let packets = ShipmentPacketService.mapShipmentDocuments(response.data);
				setGridView(packets);
			})
			.catch((error) => {
				console.log('ShipmentPacketService.searchShipmentDocuments: ERROR', error);
				if (error?.code == "ERR_NETWORK") {
					setErrorMessage(AppConfig.apiConnectionErrorMessage)
				}
				else {
					setErrorMessage(ErrorProvider.getError(error))
				}
			})
			.finally(() => {
				setIsSearchingShipmentDocuments(false);
			});
	}

	const handleStartDateChange = (value: dayjs.Dayjs | null) => {
		setStartDate(value);
	}

	const handleEndDateChange = (value: dayjs.Dayjs | null) => {
		setEndDate(value);
	}

	const handleGenerateAndViewDocument = () => {
		setErrorMessage(undefined);
		setWorkingModalParams({ params: { content: "Combining documents..." } });
		setGeneratedDocument(undefined);
		const documentIds = [...combinedDocumentContents].sort((a, b) => UtilityService.compareNumbers(a.ordinal, b.ordinal)).map((doc) => doc.documentId);

		DocumentService.generateCombinedDocument(documentIds)
			.then((response) => {
				console.log('DocumentService.generateCombinedDocument: SUCCESS', response);
				setGeneratedDocument(response.data);
			})
			.catch((error) => {
				console.log('DocumentService.generateCombinedDocument: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
				window.scrollTo(0, 0);
			})
			.finally(() => {
				setWorkingModalParams(undefined);
			});
	}

	const handleZipAndDownloadDocuments = () => {
		setErrorMessage(undefined);
		setWorkingModalParams({ params: { content: "Collecting shipment documents..." } });
		setIsProcessingOutput(true);
		const documentIds = [...combinedDocumentContents].map((doc) => doc.documentId);

		DocumentService.zipShipmentPacketDocument(documentIds)
			.then((response) => {
				console.log('DocumentService.zipShipmentPacketDocument: SUCCESS', response);
				downloadFromByteArray(response.data);
			})
			.catch((error) => {
				console.log('DocumentService.zipShipmentPacketDocument: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
				window.scrollTo(0, 0);
			})
			.finally(() => {
				setIsProcessingOutput(false);
				setWorkingModalParams(undefined);
			});
	}

	const handleExportTableToExcel = () => {
		setErrorMessage(undefined);
		setWorkingModalParams({ params: { content: "Exporting table contents..." } });

		DocumentService.exportShipmentPacketTableToExcel([...combinedDocumentContents])
			.then((response) => {
				console.log('DocumentService.exportShipmentPacketTableToExcel: SUCCESS', response);
				downloadFromByteArray(response.data);
			})
			.catch((error) => {
				console.log('DocumentService.exportShipmentPacketTableToExcel: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
				window.scrollTo(0, 0);
			})
			.finally(() => {
				setIsProcessingOutput(false);
				setWorkingModalParams(undefined);
			});
	}

	const downloadFromByteArray = (file: IGeneratedDocument): void => {
		const uint8ArrayData = new Uint8Array(Buffer.from(file.contents, 'base64'));
		const blob = new Blob([uint8ArrayData], { type: file.contentType });
		const url = window.URL.createObjectURL(blob);

		// Create a hidden anchor element to trigger the download
		const link = document.createElement('a');
		link.href = url;
		link.download = file.name;
		link.style.visibility = 'hidden';
		document.body.appendChild(link);

		// Simulate a click to start the download
		link.click();

		// Clean up the DOM and revoke the object URL (after a slight delay)
		document.body.removeChild(link);
		// A delay is often necessary to ensure the browser has initiated the download
		setTimeout(() => {
			window.URL.revokeObjectURL(url);
		}, 100);
	}

	const isCombinedListValid = (): boolean => {
		if (combinedDocumentContents.length < 2) return false;
		if (combinedDocumentContents.some((doc) => doc.isVerifying)) return false;
		if (!combinedDocumentContents.every((doc) => doc.isAvailable == true)) return false;
		return true;
	}

	const isVerifyingDocuments = (): boolean => {
		return combinedDocumentContents.some((doc) => doc.isVerifying);
	}

	return (
		<div className="hwi-shipment-packet-list">
			<Navigation></Navigation>
			<Container maxWidth="lg" className="route-container">
				<PermissionGate permission={true}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<section className="hwi-entity-search-bar">
							<div className="search-header">
								<div className="title">Search Shipment Documents</div>
								<div className="controls">
									<div className="start-date-field"><DatePicker className="start-date-field" label="Created on or after" value={dayjs(startDate)} onChange={(value) => handleStartDateChange(value)} /></div>
									<div className="end-date-field"><DatePicker className="end-date-field" label="Created on or before" value={dayjs(endDate)} onChange={(value) => handleEndDateChange(value)} /></div>
									<div><Button variant="outlined" onClick={searchShipmentDocuments}>Search</Button></div>
								</div>
							</div>
							<section className="search-options">
								<FormControl size="small">
									<InputLabel>Document Type</InputLabel>
									<Select
										value={documentTypeFilter}
										label="Document Type"
										disabled={!shipmentEntityId}
										onChange={(event) => { setDocumentTypeFilter(event.target.value) }}
									>
										<MenuItem value="All" key="-1">{documentTypeFilter == 'All' ? 'All' : '(Reset Filter)'}</MenuItem>
										{documentTypeFilterOptions.filter((item) => includedDocumentTypeIds.includes(item.value) || item.value === documentTypeFilter).map((item, index) => (
											<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
										))}
									</Select>
								</FormControl>
								<FormControl size="small">
									<TextField className="bol-field" label="BOL #" name="bol" value={bolFilter} size="small" onChange={(event) => { setBolFilter(event.target.value) }}></TextField>
								</FormControl>
								<FormControl size="small">
									<InputLabel>Customer {isLoadingValueSets ? "(Loading...)" : ""}</InputLabel>
									<Select
										value={customerFilter}
										label={`Customer${isLoadingValueSets ? " (Loading...)" : ""}`}
										onChange={(event) => { setCustomerFilter(event.target.value) }}
									>
										<MenuItem value="All" key="-1">{customerFilter == 'All' ? 'All' : '(Reset Filter)'}</MenuItem>
										{customerFilterOptions.filter((item) => includedCustomerIds.includes(item.value) || item.value === customerFilter).map((item, index) => (
											<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
										))}
									</Select>
								</FormControl>
								<FormControl size="small">
									<InputLabel>Vendor {isLoadingValueSets ? "(Loading...)" : ""}</InputLabel>
									<Select
										value={vendorFilter}
										label={`Vendor${isLoadingValueSets ? " (Loading...)" : ""}`}
										onChange={(event) => { setVendorFilter(event.target.value) }}

									>
										<MenuItem value="All" key="-1">{vendorFilter == 'All' ? 'All' : '(Reset Filter)'}</MenuItem>
										{vendorFilterOptions.filter((item) => includedVendorIds.includes(item.value) || item.value === vendorFilter).map((item, index) => (
											<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
										))}
									</Select>
								</FormControl>
							</section>
						</section>
						<section className="results">
							{errorMessage &&
								<Alert className="alert-error"
									severity="error"
									action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>
									{errorMessage}
								</Alert>
							}
							<ShipmentPacketTable
								data={gridView}
								isLoading={isSearchingShipmentDocuments}
								documentTypeFilter={documentTypeFilter}
								bolFilter={bolFilter}
								customerFilter={customerFilter}
								vendorFilter={vendorFilter}
								startDate={startDate}
								endDate={endDate}
								documentContentsChanged={(contents) => { setCombinedDocumentContents(contents); }}
							></ShipmentPacketTable>
							<div className="generate-controls">
								<div className="progress-indicator">
									{isVerifyingDocuments() &&
										<>
											<CircularProgress size="20px" color="primary"></CircularProgress>
											<span className="caption">Verifying selected documents...</span>
										</>
									}
								</div>
								<div className="actions">
									<Button size="medium" variant="contained" color="success" disabled={!isCombinedListValid() || isProcessingOutput} onClick={handleExportTableToExcel}>Export Table to Excel</Button>
									<Button size="medium" variant="contained" color="primary" disabled={!isCombinedListValid() || isProcessingOutput} onClick={handleZipAndDownloadDocuments}>Zip and Download Documents</Button>
									<Button size="medium" variant="contained" color="primary" disabled={!isCombinedListValid() || isProcessingOutput} onClick={handleGenerateAndViewDocument}>Combine and View Documents</Button>
								</div>
							</div>
						</section>
						<section className="viewer">
							<GeneratedDocumentViewer generatedDocument={generatedDocument} />
						</section>
					</LocalizationProvider>
				</PermissionGate>
			</Container>
			<WorkingModal params={workingModalParams?.params}></WorkingModal>
		</div>
	);
};

export default ShipmentPacketList;